import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  Dimensions,
  Linking,
  Platform,
  ActivityIndicator,
} from 'react-native';
// react-native-maps does not work on web - conditionally import
let MapView, Marker, PROVIDER_GOOGLE;
if (Platform.OS !== 'web') {
  const RNMaps = require('react-native-maps');
  MapView = RNMaps.default;
  Marker = RNMaps.Marker;
  PROVIDER_GOOGLE = RNMaps.PROVIDER_GOOGLE;
}
// Google Maps JS API for web
let GoogleMap, LoadScript, MarkerF;
if (Platform.OS === 'web') {
  const GMaps = require('@react-google-maps/api');
  GoogleMap = GMaps.GoogleMap;
  LoadScript = GMaps.LoadScript;
  MarkerF = GMaps.MarkerF;
}
const isWeb = Platform.OS === 'web';
import { Ionicons } from '@expo/vector-icons';
import { rideService } from '../../services/rideService';

const { width } = Dimensions.get('window');

const GOOGLE_MAPS_API_KEY = 'AIzaSyD7c3Zwn9xAZ8byihOVxmMVIeczabqHLFU';

const DARK_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const DRIVER_MARKER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
  <circle cx="22" cy="22" r="20" fill="#000000" stroke="#D4AF37" stroke-width="2"/>
  <path d="M30 24l-1.5-4.5A2 2 0 0 0 26.6 18h-9.2a2 2 0 0 0-1.9 1.5L14 24v6a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1h10v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-6zm-13.5-1l1-3h9l1 3h-11zm-0.5 4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm12 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" fill="#ffffff"/>
</svg>`;

// Helper: render Google Map with or without LoadScript depending on whether API is already loaded
const renderWebMap = (children, loadingEl) => {
  if (typeof window !== 'undefined' && window.google?.maps) {
    // Google Maps JS API already loaded (e.g. by MapScreen) — render directly
    return children;
  }
  // First load — need LoadScript
  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} loadingElement={loadingEl}>
      {children}
    </LoadScript>
  );
};

const showAlert = (title, message) => {
  if (isWeb) {
    window.alert(`${title}\n${message}`);
  } else {
    const { Alert } = require('react-native');
    Alert.alert(title, message);
  }
};

const TrackDriverScreen = ({ route, navigation }) => {
  const { ride: initialRide } = route.params;
  const [ride, setRide] = useState(initialRide);
  const [driverLocation, setDriverLocation] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const prevStatusRef = useRef(initialRide.status);
  const pollRef = useRef(null);

  const checkRideStatus = useCallback(async () => {
    try {
      const response = await rideService.getRideById(ride.id);

      if (response.success) {
        const updatedRide = response.data.ride;

        // Update driver location from real data
        if (updatedRide.driver?.currentLatitude && updatedRide.driver?.currentLongitude) {
          setDriverLocation({
            latitude: updatedRide.driver.currentLatitude,
            longitude: updatedRide.driver.currentLongitude,
          });
        }

        // Always update ride data (driver info, etc.)
        setRide(updatedRide);

        // Handle status transitions
        const prevStatus = prevStatusRef.current;
        if (updatedRide.status !== prevStatus) {
          prevStatusRef.current = updatedRide.status;

          if (updatedRide.status === 'ACCEPTED') {
            showAlert('Driver Found!', 'Your driver is on the way');
          } else if (updatedRide.status === 'ARRIVED') {
            showAlert('Driver Arrived', 'Your driver has arrived at the pickup location');
          } else if (updatedRide.status === 'IN_PROGRESS') {
            showAlert('Ride Started', 'You are on your way!');
          } else if (updatedRide.status === 'COMPLETED') {
            navigation.replace('RateRide', { ride: updatedRide });
          } else if (updatedRide.status === 'CANCELLED') {
            showAlert('Ride Cancelled', updatedRide.cancelledBy === 'DRIVER' ? 'The driver cancelled the ride.' : 'Ride was cancelled.');
            navigation.navigate('MainTabs');
          }
        }
      }
    } catch (error) {
      console.error('[TrackDriver] Error checking ride status:', error.message);
    }
  }, [ride.id, navigation]);

  useEffect(() => {
    checkRideStatus(); // Initial fetch
    pollRef.current = setInterval(checkRideStatus, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [checkRideStatus]);

  const handleCancelRide = async () => {
    // Use window.confirm on web, Alert.alert on native
    if (isWeb) {
      const confirmed = window.confirm('Are you sure you want to cancel this ride?');
      if (!confirmed) return;
    } else {
      // On native, we need Alert.alert with callback - but we restructure for reliability
      const { Alert } = require('react-native');
      return new Promise((resolve) => {
        Alert.alert(
          'Cancel Ride',
          'Are you sure you want to cancel this ride?',
          [
            { text: 'No', style: 'cancel', onPress: () => resolve(false) },
            { text: 'Yes', style: 'destructive', onPress: () => resolve(true) },
          ]
        );
      }).then(async (confirmed) => {
        if (!confirmed) return;
        await performCancel();
      });
    }

    // Web path continues here
    await performCancel();
  };

  const performCancel = async () => {
    setCancelling(true);
    try {
      const response = await rideService.cancelRide(ride.id);
      if (response.success) {
        showAlert('Cancelled', response.message || 'Ride cancelled successfully');
        navigation.navigate('MainTabs');
      } else {
        showAlert('Error', response.message || 'Failed to cancel ride');
      }
    } catch (error) {
      console.error('[TrackDriver] Cancel error:', error);
      showAlert('Error', error.response?.data?.message || 'Failed to cancel ride');
    } finally {
      setCancelling(false);
    }
  };

  const handleCallDriver = () => {
    if (ride.driver?.phone) {
      Linking.openURL(`tel:${ride.driver.phone}`);
    } else {
      showAlert('Unavailable', 'Driver phone number not available');
    }
  };

  const getStatusText = () => {
    switch (ride.status) {
      case 'REQUESTED': return 'Looking for a driver...';
      case 'ACCEPTED': return 'Driver is on the way';
      case 'ARRIVED': return 'Driver has arrived';
      case 'IN_PROGRESS': return 'Ride in progress';
      case 'COMPLETED': return 'Ride completed';
      case 'CANCELLED': return 'Ride cancelled';
      default: return 'Processing...';
    }
  };

  const getStatusColor = () => {
    switch (ride.status) {
      case 'REQUESTED': return '#FF9800';
      case 'ACCEPTED': return '#2196F3';
      case 'ARRIVED': return '#9C27B0';
      case 'IN_PROGRESS': return '#4CAF50';
      case 'COMPLETED': return '#4CAF50';
      case 'CANCELLED': return '#F44336';
      default: return '#666666';
    }
  };

  // Compute ETA based on timestamps and status
  const getEtaText = () => {
    if (ride.status === 'ACCEPTED') {
      // If we have acceptedAt, show estimated arrival (5-10 min after accept)
      if (ride.acceptedAt) {
        const accepted = new Date(ride.acceptedAt);
        const etaMs = accepted.getTime() + 8 * 60 * 1000; // 8 min estimate
        const now = Date.now();
        const remainingMin = Math.max(1, Math.round((etaMs - now) / 60000));
        return `~${remainingMin} min to pickup`;
      }
      return '~5-10 min to pickup';
    } else if (ride.status === 'ARRIVED') {
      return 'Driver is at pickup';
    } else if (ride.status === 'IN_PROGRESS') {
      if (ride.startedAt) {
        const started = new Date(ride.startedAt);
        const dist = ride.distance || 5;
        const etaMin = Math.max(3, Math.round(dist * 2.5)); // ~2.5 min per mile
        const etaMs = started.getTime() + etaMin * 60 * 1000;
        const now = Date.now();
        const remainingMin = Math.max(1, Math.round((etaMs - now) / 60000));
        return `~${remainingMin} min to destination`;
      }
      return 'En route to destination';
    } else if (ride.status === 'REQUESTED') {
      return 'Matching you with a driver...';
    }
    return '';
  };

  const driverName = ride.driver?.name || 'Finding driver...';
  const driverCar = ride.driver
    ? `${ride.driver.vehicleMake || ''} ${ride.driver.vehicleModel || 'Vehicle'} ${ride.driver.vehicleColor ? '(' + ride.driver.vehicleColor + ')' : ''} ${ride.driver.vehiclePlate ? '• ' + ride.driver.vehiclePlate : ''}`.trim()
    : '';
  const driverRating = ride.driver?.driverRating ? `${ride.driver.driverRating} ★` : '';

  // Map region centered on pickup or driver
  const mapRegion = driverLocation
    ? { ...driverLocation, latitudeDelta: 0.02, longitudeDelta: 0.02 }
    : ride.pickupLat && ride.pickupLng
      ? { latitude: ride.pickupLat, longitude: ride.pickupLng, latitudeDelta: 0.05, longitudeDelta: 0.05 }
      : { latitude: 37.78825, longitude: -122.4324, latitudeDelta: 0.05, longitudeDelta: 0.05 };

  const etaText = getEtaText();

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mapContainer}>
        {isWeb ? (
          renderWebMap(
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={{
                lat: driverLocation ? driverLocation.latitude : (ride.pickupLat ? parseFloat(ride.pickupLat) : 37.78825),
                lng: driverLocation ? driverLocation.longitude : (ride.pickupLng ? parseFloat(ride.pickupLng) : -122.4324),
              }}
              zoom={15}
              options={{
                styles: DARK_MAP_STYLE,
                disableDefaultUI: true,
                zoomControl: true,
                gestureHandling: 'greedy',
              }}
            >
              {/* Driver marker */}
              {driverLocation && (
                <MarkerF
                  position={{ lat: driverLocation.latitude, lng: driverLocation.longitude }}
                  title={driverName}
                  icon={{
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(DRIVER_MARKER_SVG)}`,
                    scaledSize: { width: 44, height: 44 },
                    anchor: { x: 22, y: 22 },
                  }}
                />
              )}
              {/* Pickup marker (green) */}
              {ride.pickupLat && ride.pickupLng && (
                <MarkerF
                  position={{ lat: parseFloat(ride.pickupLat), lng: parseFloat(ride.pickupLng) }}
                  title="Pickup"
                  icon={{
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#4CAF50" stroke="#fff" stroke-width="2"/><circle cx="12" cy="12" r="4" fill="#fff"/></svg>')}`,
                    scaledSize: { width: 24, height: 24 },
                    anchor: { x: 12, y: 12 },
                  }}
                />
              )}
              {/* Dropoff marker (red) */}
              {ride.dropoffLat && ride.dropoffLng && (
                <MarkerF
                  position={{ lat: parseFloat(ride.dropoffLat), lng: parseFloat(ride.dropoffLng) }}
                  title="Dropoff"
                  icon={{
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#F44336" stroke="#fff" stroke-width="2"/><circle cx="12" cy="12" r="4" fill="#fff"/></svg>')}`,
                    scaledSize: { width: 24, height: 24 },
                    anchor: { x: 12, y: 12 },
                  }}
                />
              )}
            </GoogleMap>,
            <View style={styles.webMapPlaceholder}>
              <ActivityIndicator size="large" color="#D4AF37" />
              <Text style={styles.webMapText}>Loading map...</Text>
            </View>
          )
        ) : (
          <MapView
            style={styles.map}
            provider={PROVIDER_GOOGLE}
            region={mapRegion}
            showsUserLocation={true}
          >
            {driverLocation && (
              <Marker coordinate={driverLocation} title="Your Driver" description={driverName}>
                <View style={styles.driverMarker}>
                  <Ionicons name="car" size={20} color="#ffffff" />
                </View>
              </Marker>
            )}
            {ride.pickupLat && ride.pickupLng && (
              <Marker
                coordinate={{ latitude: ride.pickupLat, longitude: ride.pickupLng }}
                title="Pickup"
                pinColor="green"
              />
            )}
            {ride.dropoffLat && ride.dropoffLng && (
              <Marker
                coordinate={{ latitude: ride.dropoffLat, longitude: ride.dropoffLng }}
                title="Dropoff"
                pinColor="red"
              />
            )}
          </MapView>
        )}
      </View>

      <View style={styles.statusPanel}>
        {/* Status + ETA */}
        <View style={styles.statusHeader}>
          <View style={[styles.statusDot, { backgroundColor: getStatusColor() }]} />
          <View style={styles.statusHeaderText}>
            <Text style={styles.statusText}>{getStatusText()}</Text>
            {etaText ? <Text style={styles.etaText}>{etaText}</Text> : null}
          </View>
        </View>

        {/* Driver Info */}
        {ride.driver && ['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(ride.status) && (
          <View style={styles.driverInfo}>
            <View style={styles.driverAvatar}>
              <Ionicons name="person" size={24} color="#666666" />
            </View>
            <View style={styles.driverDetails}>
              <Text style={styles.driverName}>{driverName}</Text>
              <Text style={styles.driverCar}>{driverCar}</Text>
              {driverRating ? <Text style={styles.driverRating}>{driverRating}</Text> : null}
            </View>
            <View style={styles.driverActions}>
              <TouchableOpacity style={styles.actionButton} onPress={handleCallDriver}>
                <Ionicons name="call" size={20} color="#4CAF50" />
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Trip Details */}
        <View style={styles.tripInfo}>
          <View style={styles.locationRow}>
            <View style={styles.pickupDot} />
            <Text style={styles.locationText} numberOfLines={1}>{ride.origin}</Text>
          </View>
          <View style={styles.locationRow}>
            <View style={styles.destinationDot} />
            <Text style={styles.locationText} numberOfLines={1}>{ride.destination}</Text>
          </View>
          {ride.estimatedFare != null && (
            <Text style={styles.fareText}>Estimated Fare: ${Number(ride.estimatedFare).toFixed(2)}</Text>
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          {['REQUESTED', 'ACCEPTED', 'ARRIVED'].includes(ride.status) && (
            <TouchableOpacity
              style={[styles.cancelButton, cancelling && { opacity: 0.5 }]}
              onPress={handleCancelRide}
              disabled={cancelling}
            >
              {cancelling ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={styles.cancelButtonText}>Cancel Ride</Text>
              )}
            </TouchableOpacity>
          )}
          {ride.status === 'COMPLETED' && (
            <TouchableOpacity
              style={styles.doneButton}
              onPress={() => navigation.replace('RateRide', { ride })}
            >
              <Text style={styles.doneButtonText}>Rate Ride</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#ffffff' },
  mapContainer: { flex: 1 },
  map: { width, height: '100%' },
  webMapPlaceholder: {
    flex: 1, backgroundColor: '#f0f0f0', justifyContent: 'center',
    alignItems: 'center', padding: 20,
  },
  webMapText: { color: '#333', fontSize: 20, fontWeight: '700', marginTop: 12 },
  webMapEta: { color: '#2196F3', fontSize: 16, fontWeight: '600', marginTop: 8 },
  webMapSubtext: { color: '#666', fontSize: 14, marginTop: 6, textAlign: 'center' },
  driverMarker: {
    backgroundColor: '#000000', borderRadius: 20, width: 40, height: 40,
    justifyContent: 'center', alignItems: 'center',
  },
  statusPanel: {
    backgroundColor: '#ffffff', borderTopLeftRadius: 20, borderTopRightRadius: 20,
    paddingHorizontal: 20, paddingVertical: 20, elevation: 10,
    shadowColor: '#000', shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.25, shadowRadius: 3.84,
  },
  statusHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 15 },
  statusDot: { width: 12, height: 12, borderRadius: 6, marginRight: 10 },
  statusHeaderText: { flex: 1 },
  statusText: { fontSize: 18, fontWeight: 'bold', color: '#333333' },
  etaText: { fontSize: 14, color: '#2196F3', fontWeight: '600', marginTop: 2 },
  driverInfo: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#F8F8F8',
    borderRadius: 12, padding: 15, marginBottom: 15,
  },
  driverAvatar: {
    width: 50, height: 50, borderRadius: 25, backgroundColor: '#E0E0E0',
    justifyContent: 'center', alignItems: 'center', marginRight: 15,
  },
  driverDetails: { flex: 1 },
  driverName: { fontSize: 16, fontWeight: 'bold', color: '#333333', marginBottom: 2 },
  driverCar: { fontSize: 14, color: '#666666', marginBottom: 2 },
  driverRating: { fontSize: 14, color: '#FF9800', fontWeight: '500' },
  driverActions: { flexDirection: 'row' },
  actionButton: {
    width: 40, height: 40, borderRadius: 20, backgroundColor: '#ffffff',
    justifyContent: 'center', alignItems: 'center', marginLeft: 10,
    elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22, shadowRadius: 2.22,
  },
  tripInfo: { marginBottom: 15 },
  locationRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  pickupDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#4CAF50', marginRight: 15 },
  destinationDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#F44336', marginRight: 15 },
  locationText: { flex: 1, fontSize: 14, color: '#666666' },
  fareText: { fontSize: 16, fontWeight: 'bold', color: '#333', marginTop: 5, textAlign: 'center' },
  actionButtons: { flexDirection: 'row', justifyContent: 'center' },
  cancelButton: {
    backgroundColor: '#F44336', borderRadius: 8, paddingVertical: 14,
    paddingHorizontal: 40, minWidth: 160, alignItems: 'center',
  },
  cancelButtonText: { color: '#ffffff', fontSize: 16, fontWeight: 'bold' },
  doneButton: { backgroundColor: '#000000', borderRadius: 8, paddingVertical: 14, paddingHorizontal: 40 },
  doneButtonText: { color: '#ffffff', fontSize: 16, fontWeight: 'bold' },
});

export default TrackDriverScreen;
